import React from 'react';
import { Link } from 'gatsby';
import { processLink } from '../../shared/processLink';
import { useLayoutContext } from '../../providers/LayoutProvider';
import { useParentNodeProvider } from '../../providers/ParentNodeProvider';
import {
    EventName,
    UserInteractionEvent,
    UserInteractionType,
} from '../../models/GTMEventTypes';
import { onClickEventTracker } from '../../shared/analytics/callAnalytics';
import resolvePath from '../../shared/resolvePath';

export type SmartLinkProps = React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
>;

export const SmartLink = (props: SmartLinkProps) => {
    const { nodeLookup } = useLayoutContext();
    const { nodeName: pNodeName, nodeType: pNodeType } =
        useParentNodeProvider();

    const fixedUrl: string = processLink(props.href, nodeLookup);

    const isInternalLink =
        (fixedUrl.startsWith(`/`) || fixedUrl.startsWith(`#`)) &&
        !fixedUrl.includes(`.`);

    // Use a passed ref to the anchor element to determine the component name based on innerHTML or Alt text
    const getComponentName = (
        ref: React.MutableRefObject<HTMLAnchorElement>
    ) => {
        return ref?.current?.innerText !== ``
            ? ref?.current?.innerText
            : ref?.current?.innerHTML.includes(`img`)
            ? ref?.current?.children.item(0)?.getAttribute(`alt`)
            : ``;
    };

    const ref = React.useRef<HTMLAnchorElement>(null);

    // Populate meta data for tracking the user interaction event
    const eventAnalytics: UserInteractionEvent = {
        event: EventName.USER_INTERACTION,
        event_data: {
            type: UserInteractionType.LINK_CLICK,
            component_name: getComponentName(ref),
            component_type: isInternalLink ? `Internal Link` : `External Link`,
            parent_name: pNodeName,
            parent_type: pNodeType,
            current_path: resolvePath(),
            next_path: fixedUrl,
        },
    };

    // Wrap the event-tracking onClick handler to simplify passing it as a prop
    const onClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
        onClickEventTracker(e, eventAnalytics, props.onClick);

    // Pass the ref and onClick handlers as named props to the appropriate wrapper
    const Wrapper = !isInternalLink
        ? React.createElement(
              `a`,
              { ...props, onClick, ref, target: `_blank` },
              props.children
          )
        : React.createElement(
              Link,
              { ...props, to: fixedUrl, onClick, ref },
              props.children
          );

    return Wrapper;
};
